import type { ServerMessage } from '../shared/protocol.js';
import type { MapDefinition, PublicRoom, RaceSnapshot } from '../shared/types.js';
import { t } from './i18n/index.js';

export interface SpectatorState {
  readonly room: PublicRoom | null;
  readonly map: MapDefinition | null;
  readonly snapshot: RaceSnapshot | null;
}

export type SpectatorAction =
  | { type: 'render'; immediate: boolean }
  | { type: 'toast'; message: string; tone: 'info' | 'error' }
  | { type: 'transfer'; room: PublicRoom; inviteToken: string }
  | { type: 'error'; message: string };

/** Typed reducer for the spectator session; DOM and routing stay with the app. */
export class SpectatorController {
  private stateValue: SpectatorState = { room: null, map: null, snapshot: null };

  get state(): SpectatorState {
    return this.stateValue;
  }

  begin(room: PublicRoom, map: MapDefinition | null): void {
    this.stateValue = { room, map, snapshot: null };
  }

  reset(): void {
    this.stateValue = { room: null, map: null, snapshot: null };
  }

  reduce(message: ServerMessage, serverTime: number): SpectatorAction[] {
    if (message.type === 'error') return [{ type: 'error', message: message.message }];
    if (message.type === 'party-transfer') {
      return [{ type: 'transfer', room: message.room, inviteToken: message.inviteToken }];
    }
    if (message.type === 'spectator-welcome') {
      this.stateValue = {
        room: message.room,
        map: message.map,
        snapshot: {
          type: 'snapshot',
          sequence: 0,
          serverTime,
          phase: message.room.phase,
          countdownMs: 0,
          racers: message.racers,
          projectiles: [],
          impacts: [],
        },
      };
      return [{ type: 'render', immediate: true }];
    }
    if (message.type === 'room-state') {
      this.stateValue = { ...this.stateValue, room: message.room };
      return [{ type: 'render', immediate: true }];
    }
    if (message.type === 'snapshot') {
      const room = this.stateValue.room && this.stateValue.room.phase !== message.phase
        ? { ...this.stateValue.room, phase: message.phase }
        : this.stateValue.room;
      this.stateValue = { ...this.stateValue, room, snapshot: message };
      return [{ type: 'render', immediate: false }];
    }
    if (message.type === 'player-joined') {
      return [{ type: 'toast', message: t('{name} joined the grid', { name: message.racer.name }), tone: 'info' }];
    }
    if (message.type === 'player-left') {
      return [{ type: 'toast', message: t('A driver lost their link — their slot is being held.'), tone: 'info' }];
    }
    return [];
  }
}
